<template>
  <div class="wrapper" ref="colorPickerWrapper">
    <div
      :class="`picker-wrapper picker-wrapper--${position} ${hideAlphaSlider ? 'hide-alpha-slider' : ''}`"
      v-show="active">
      <v-color-picker
        :value="value"
        class="mx-auto"
        dot-size="20"
        hide-mode-switch
        mode="hexa"
        elevation="0"
        @update:color="updateColor">
      </v-color-picker>
    </div>
    <v-text-field
      hide-details
      v-model="textValue"
      @mouseenter="$emit('is-hovered', true)"
      @mouseleave="$emit('is-hovered', false)"
      @focus="toggleColorPicker(true)"
      @change="handleColorChange">
      <template v-slot:label> {{ componentTitle }}</template>
      <template v-slot:append>
        <div
          class="field-picker__color-preview cursor-pointer"
          :style="{
            'background-color': value,
          }"></div>
      </template>
    </v-text-field>
  </div>
</template>

<script>
  import { ref, watch } from "@vue/composition-api";
  import { onClickOutside } from "@vueuse/core";

  export default {
    name: "VaColorPicker",
    props: {
      value: {
        type: String,
        default: "#FFFFFF",
      },
      title: {
        type: String,
        required: false,
        default: "",
      },
      mode: {
        type: String,
        default: "hex",
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      position: {
        type: String,
        default: "top",
      },
      hideAlphaSlider: {
        type: Boolean,
        default: false,
      },
    },
    setup(props, { emit }) {
      const componentTitle = ref(props.title);
      const textValue = ref(props.value);
      const active = ref(false);
      const colorPickerWrapper = ref(null);
      const emitCount = ref(0);

      const updateColor = colorCode => {
        // updateColor method is called when v-color-picker is mounted. It automatically emits
        // #FF0000 value, thus the va-color-picker component cannot have empty string as a value
        // (let's say in cases when we need to send empty value instead of some default color).
        // We have to skip the first emit if the color is the default color that vuetify sets.
        if (colorCode[props.mode] === "#FF0000" && emitCount.value === 0) {
          return;
        }

        emitCount.value++;
        emit("input", colorCode[props.mode]);
      };

      const toggleColorPicker = state => {
        active.value = state;
        emit("is-active", state);
      };

      const handleColorChange = value => {
        emit("input", value);
      };

      onClickOutside(colorPickerWrapper, () => {
        toggleColorPicker(false);
      });

      watch(
        () => props.value,
        () => {
          textValue.value = props.value;
        },
      );

      return {
        colorPickerWrapper,
        componentTitle,
        updateColor,
        toggleColorPicker,
        handleColorChange,
        textValue,
        active,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  .field-picker__color-preview {
    width: 24px;
    height: 24px;
    border: 1px solid get-color("border");
    border-radius: 50%;
    margin-right: 5px;
  }

  .wrapper {
    position: relative;
    width: 100%;
  }

  @include theme(v-application) using ($material) {
    .picker-wrapper {
      position: absolute;
      z-index: 50;
      filter: drop-shadow(0px 2px 10px map-deep-get($material, "shadow-color-secondary"));
      border-radius: 4px;
      padding: 10px;
      background-color: get-color("element-bg-100");
      right: 0;

      &--top {
        top: -320px;

        &::after {
          bottom: -10px;
          right: 11px;
          border-top: 10px solid get-color("element-bg-100");
        }
      }

      &.hide-alpha-slider {
        &::v-deep {
          .v-color-picker__alpha {
            display: none;
          }

          .v-color-picker__hue {
            margin-bottom: 0 !important;
          }
        }
      }

      &--bottom {
        bottom: -320px;

        &::after {
          top: -10px;
          right: 11px;
          border-bottom: 10px solid get-color("element-bg-100");
        }
      }

      .v-color-picker {
        background-color: get-color("element-bg-100");
      }

      &::after {
        content: "";
        position: absolute;
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
      }
    }
  }
</style>
